/**
 * SA-1047: the pure rules for a customer reply that answers a sent quote.
 * No React, no store. Words live in customerUpdateText.ts.
 */
import type { CustomerUpdateSummary, QuoteVersion } from '@/types/commerce'
import {
  MESSAGES_WAITING_LABEL, messagesWaitingWords, RESPONSE_WAITING_LABEL, responseWaitingWords,
  UPDATE_WAITING_LABEL, updateWaitingWords,
} from './customerUpdateText'
import { recordQuote } from './quoteText'

/** The TS twin of the server's RESPONSE_KIND_OF: the quote response kind an intent records as. */
const RESPONSE_KIND_OF: Record<string, 'counter' | 'accepted' | 'declined'> = {
  countered: 'counter',
  accepted: 'accepted',
  declined: 'declined',
}

export function responseKindOf(intent: string): 'counter' | 'accepted' | 'declined' | null {
  return RESPONSE_KIND_OF[intent] ?? null
}

export type CannotRecord =
  | { reason: 'closed' }
  | { reason: 'not_sent' }
  | { reason: 'before_send'; version: number }
  | { reason: 'answered'; version: number }
  | { reason: 'refused' }

/** The time from which a reply can answer a version (the server's `order_quotes.answered_from`): a version
 * marked sent by hand stamps `sentAt` with the click, often after the customer answered, so it reads from
 * `preparedAt`; an emailed one from `sentAt`. */
export function answeredFrom(v: QuoteVersion): string | null {
  if (!v.sentAt) return null
  return v.sentVia === 'manual' && v.preparedAt ? v.preparedAt : v.sentAt
}

/** Why a waiting response cannot be recorded now, in the spec's order; null when it can. Keyed on the record's latest version. */
export function cannotRecord(
  r: { receivedAt: string },
  q: { open: boolean; versions: QuoteVersion[]; respondRefusal: unknown | null },
): CannotRecord | null {
  if (!q.open) return { reason: 'closed' }
  const latest = recordQuote(q.versions)
  const from = latest ? answeredFrom(latest) : null
  if (!latest || !from) return { reason: 'not_sent' }
  if (Date.parse(r.receivedAt) < Date.parse(from)) return { reason: 'before_send', version: latest.versionNumber }
  if (latest.status === 'countered' || latest.status === 'accepted' || latest.status === 'declined') {
    return { reason: 'answered', version: latest.versionNumber }
  }
  if (q.respondRefusal) return { reason: 'refused' }
  return null
}

/** The register's chip for what is waiting on a record; null when nothing is. */
export function registerChip(s: CustomerUpdateSummary): { label: string; title: string } | null {
  const w = s.waiting
  const r = s.responses
  if (w > 0 && r > 0) return { label: MESSAGES_WAITING_LABEL, title: messagesWaitingWords(w + r) }
  if (w > 0) return { label: UPDATE_WAITING_LABEL, title: updateWaitingWords(w) }
  if (r > 0) return { label: RESPONSE_WAITING_LABEL, title: responseWaitingWords(r) }
  return null
}
